/* eslint-disable import/no-cycle */
/* eslint-disable react/require-default-props */
/* eslint-disable react-hooks/exhaustive-deps */
import { Input, FormControl, FormErrorMessage, theme, SystemStyleObject } from '@chakra-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import isObject from 'lodash/isObject';
import { useGetItemProperty } from '../hooks/useGetItemProperty';
import useHireFormErrorHandling, { ValidationType, ValidationTypeEnum } from '../hooks/useHireFormErrorHandling';
import { useUpdateFormItem } from '../hooks/useUpdateFormItem';
import { FormBuilderComponentProps, TextWithTranslationsT } from '../types';
import withToggleVisibility from '../utils/withToggleVisibility';
import { useStoreState } from '../../../../models/hooks';
import { updateMissingTranslations } from '../utils';

interface LabelSchemaBuilderProps {
  fieldId: string;
  currentLanguage: string;
  value: string;
  onChange: (value: string) => void;
  // path: (string | number)[];
  isAdvanced: boolean;
  disabled?: boolean;
  placeholder?: string;
}

const LabelInputComponent: React.FC<LabelSchemaBuilderProps> = React.memo(
  ({
    fieldId,
    currentLanguage,
    value: incomingValue,
    onChange: onFinalValueCommit,
    // path: labelElementPath,
    disabled: isInteractionDisabled = false,
    placeholder,
    isAdvanced,
  }: LabelSchemaBuilderProps) => {
    const [editText, setEditText] = React.useState(incomingValue);

    const { validate, error, hasError, inputRef } = useHireFormErrorHandling({
      targetId: fieldId,
      innerPath: `label.${currentLanguage}`,
      validationType: ValidationTypeEnum.LABEL as ValidationType,
      isAdvanced,
    });

    React.useEffect(() => {
      if (validate(incomingValue)) setEditText(incomingValue);
      inputRef.current?.focus();
    }, [incomingValue]);

    const handleEditTextChange = React.useCallback(
      (newIntermediateValue: string) => {
        validate(newIntermediateValue);
        setEditText(newIntermediateValue);
      },
      [validate],
    );

    const handleEditTextCommit = React.useCallback(
      (finalCommittedText: string) => {
        onFinalValueCommit(finalCommittedText);
      },
      [onFinalValueCommit, validate],
    );

    const editInputStyles = React.useMemo(
      (): SystemStyleObject => ({
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
        paddingLeft: '3px',
        fontSize: 'xs',
        width: 'fit-content',
        minWidth: 150,
        borderRadius: 1.2,
        boxShadow: 'none !important',
      }),
      [],
    );

    return (
      <FormControl isInvalid={hasError} id={`form-control-${fieldId}`} size="xs">
        <Input
          value={editText}
          placeholder={placeholder || 'Add Label...'}
          size="xs"
          isInvalid={hasError}
          sx={editInputStyles}
          ref={inputRef}
          _placeholder={{
            color: theme.colors.gray[300],
          }}
          isDisabled={isInteractionDisabled}
          onChange={(e) => handleEditTextChange(e.target.value)}
          onBlur={(e) => handleEditTextCommit(e.target.value)}
          onKeyDown={(e) => {
            const inputElement = e.currentTarget as HTMLInputElement;

            if (e.key === 'Enter' || e.key === 'Escape') {
              handleEditTextCommit(inputElement?.value);
            }
          }}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          data-testid={`label-input-${fieldId}`}
          aria-describedby={hasError ? `error-${fieldId}` : undefined}
        />

        {hasError && error && (
          <FormErrorMessage mt={0} fontSize="xs" id={`error-${fieldId}`}>
            {error}
          </FormErrorMessage>
        )}
      </FormControl>
    );
  },
  (p, n) =>
    // eslint-disable-next-line
    p.value === n.value && p.disabled === n.disabled && p.onChange === n.onChange,
);
interface FormLabelInputComponentProps {
  isAdvanced?: boolean;
  fieldId: string;
  value: string;
  placeholder?: string;
  onBlur?: () => void;
}

const FormLabelInputComponent = (props: FormLabelInputComponentProps) => {
  const { fieldId, isAdvanced: isAdvancedLayout = false, placeholder, value: labelForLanguage, onBlur } = props;
  const handleOnChange = useUpdateFormItem();
  const targetLanguage = useStoreState((state) => state.formBuilder.currentLanguage);
  const defaultLanguage = useStoreState((state) => state.formBuilder.defaultLanguage);
  const languages = useStoreState((state) => state.formBuilder.availableLanguages);

  const currentFieldValue = useGetItemProperty<TextWithTranslationsT>(fieldId, 'label');

  const handleLabelDataChange = React.useCallback(
    (newLabelText: string) => {
      const updatedValue = {
        ...currentFieldValue,
        [targetLanguage]: newLabelText,
      };
      const missingTranslations = updateMissingTranslations({ label: updatedValue }, 'label', defaultLanguage, languages);

      handleOnChange(fieldId, missingTranslations);
      onBlur?.();
    },
    [currentFieldValue, targetLanguage, handleOnChange, defaultLanguage, languages],
  );

  if (isAdvancedLayout && !labelForLanguage) return null;

  return (
    <LabelInputComponent
      fieldId={fieldId}
      currentLanguage={targetLanguage}
      value={labelForLanguage || ''}
      onChange={handleLabelDataChange}
      // path={fullPathToLabelData}
      disabled={isAdvancedLayout}
      placeholder={placeholder}
      isAdvanced={isAdvancedLayout}
    />
  );
};
const LabelInput = withToggleVisibility(FormLabelInputComponent);

const FormLabelInput = (props: Pick<FormBuilderComponentProps, 'isAdvanced' | 'fieldId'>) => {
  const { t } = useTranslation('hrFormTemplate');
  const { fieldId, isAdvanced: isAdvancedLayout = false } = props;
  // const baseElementPath = useGetPathForFieldId(fieldId);
  const currentFieldValue = useGetItemProperty<TextWithTranslationsT>(fieldId, 'label');
  const currentLanguage = useStoreState((state) => state.formBuilder.currentLanguage);

  // const fullPathToLabelData = React.useMemo(() => {
  //   return [...baseElementPath, 'label', currentLanguage];
  // }, [baseElementPath, currentLanguage]);

  return (
    <LabelInput
      initialValue={currentFieldValue!}
      validate={(callback, value, language) => {
        if (isObject(value)) callback(value?.[language || '']);
      }}
      currentLanguage={currentLanguage}
      isDisabled={isAdvancedLayout}
      // path={fullPathToLabelData}
      isAdvanced={isAdvancedLayout}
      fieldId={fieldId}
      innerPath={`label.${currentLanguage}`}
      placeholder={t('formBuilder.addLabel')}
      validationType={ValidationTypeEnum.LABEL as ValidationType}
    />
  );
};

export default FormLabelInput;
